import { Component, OnInit, OnDestroy, inject, ViewChildren, QueryList, ElementRef, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { Subscription } from 'rxjs';
import { sortEnvironments, validateProviderEnvUrl, getUrlValidationErrorMessage } from 'src/app/services/util.service';
import { VbDialogComponent } from '../../ui-components/vb-dialog/vb-dialog.component';
import { VbSearchInputComponent } from '../../ui-components/vb-search-input/vb-search-input.component';
import { VbButtonComponent } from '../../ui-components/vb-button/vb-button.component';
import { VbButtonGroupComponent } from '../../ui-components/vb-button-group/vb-button-group.component';

@Component({
  selector: 'vb-provider-env-url-config',
  templateUrl: './provider-env-url-config.component.html',
  styleUrls: ['./provider-env-url-config.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SelectModule,
    InputTextModule,
    VbDialogComponent,
    VbSearchInputComponent,
    VbButtonComponent,
    VbButtonGroupComponent
  ]
})
export class ProviderEnvUrlConfigComponent implements OnInit, OnChanges, OnDestroy {
  private translateService = inject(TranslateService);
  private subscription: Subscription;

  @ViewChildren('envUrlInput') envUrlInputs: QueryList<ElementRef>;

  // Dialog visibility
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Data inputs
  @Input() environmentsData: any[] = [];
  @Input() formData: Record<string, string> = {};
  @Input() disableField = false;
  @Input() providerName = '';

  // Event outputs
  @Output() save = new EventEmitter<Record<string, string>>();
  @Output() cancelled = new EventEmitter<void>();

  environments: any[] = [];
  filteredEnvironments: any[] = [];
  formGroup: FormGroup;
  searchString = '';
  selectedFilter = 'all';

  get filterOptions() {
    return [
      { label: this.translateService.instant('ALL'), value: 'all' },
      { label: this.translateService.instant('CONFIGURED'), value: 'configured' },
      { label: this.translateService.instant('NOT_CONFIGURED'), value: 'notConfigured' }
    ];
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['environmentsData'] || changes['formData']) && !changes['environmentsData']?.firstChange) {
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    this.environments = sortEnvironments(this.environmentsData || []);
    
    this.formGroup = new FormGroup({});

    // Initialize form controls from environment data
    this.environments.forEach((env) => {
      const urlValue = this.formData?.[env.name] || '';
      const validators = [validateProviderEnvUrl];

      // Add required validator for system-generated Development environment
      if (env.purpose === 'DEVELOPMENT' && env.isSysGen) {
        validators.unshift(Validators.required);
      }

      const control = new FormControl(
        { value: urlValue, disabled: this.disableField },
        { validators: validators, updateOn: 'change' }
      );
      this.formGroup.addControl(env.name, control);
    });

    this.filteredEnvironments = [...this.environments];
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * Called when dialog is fully shown
   */
  onDialogShow(): void {
    this.focusFirstInput();
  }

  /**
   * Focus the first environment URL input field
   */
  private focusFirstInput(): void {
    // Use a slight delay to ensure dialog content is fully rendered
    setTimeout(() => {
      if (this.envUrlInputs && this.envUrlInputs.first) {
        this.envUrlInputs.first.nativeElement.focus();
      }
    }, 100);
  }

  /**
   * Filter environments based on search string and filter selection
   */
  filterEnvironments(): void {
    const search = this.searchString?.toLowerCase() || '';
    let filtered = [...this.environments];

    // Apply status filter
    if (this.selectedFilter === 'configured') {
      filtered = filtered.filter((env) => {
        const value = this.formGroup.get(env.name)?.value;
        return value && value.trim() !== '';
      });
    } else if (this.selectedFilter === 'notConfigured') {
      filtered = filtered.filter((env) => {
        const value = this.formGroup.get(env.name)?.value;
        return !value || value.trim() === '';
      });
    }

    // Apply search filter (search in both env name and URL value)
    if (search) {
      filtered = filtered.filter((env) => {
        const envNameMatches = env.name.toLowerCase().includes(search);
        const urlValue = this.formGroup.get(env.name)?.value || '';
        const urlMatches = urlValue.toLowerCase().includes(search);
        return envNameMatches || urlMatches;
      });
    }

    this.filteredEnvironments = filtered;
  }

  /**
   * Clear search and reset filter
   */
  clearSearch(): void {
    this.searchString = '';
    this.filterEnvironments();
  }

  /**
   * Get validation error message for a control
   */
  getErrorMessage(envName: string): string {
    const control = this.formGroup.get(envName);
    return getUrlValidationErrorMessage(control, this.translateService);
  }

  /**
   * Check if form has any validation errors or if Development environment is empty
   */
  hasErrors(): boolean {
    // Check if system-generated Development environment value is empty or invalid
    const devEnv = this.environments.find(env => env.purpose === 'DEVELOPMENT' && env.isSysGen);
    if (devEnv) {
      const devControl = this.formGroup.get(devEnv.name);
      if (devControl) {
        const devValue = devControl.value;
        if (!devValue || devValue.trim() === '' || devControl.invalid) {
          return true;
        }
      }
    }
    return this.formGroup.invalid;
  }

  /**
   * Save and close dialog
   */
  saveAndClose(): void {
    // Mark all controls as dirty to show validation errors
    Object.keys(this.formGroup.controls).forEach((key) => {
      this.formGroup.get(key)?.markAsDirty();
    });

    if (this.hasErrors()) {
      return;
    }

    const result: Record<string, string> = {};
    this.environments.forEach((env) => {
      const value = this.formGroup.get(env.name)?.value;
      if (value !== undefined && value !== null) {
        result[env.name] = value;
      }
    });

    this.save.emit(result);
    this.closeDialog();
  }

  /**
   * Close dialog without saving
   */
  closeDialog(): void {
    this.searchString = '';
    this.selectedFilter = 'all';
    this.filterEnvironments();
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  /**
   * Get dialog header text
   */
  get dialogHeader(): string {
    const baseText = this.translateService.instant('CONFIG_ENVIRONMENT_URLS_FOR');
    return this.providerName ? `${baseText} '${this.providerName}'` : baseText;
  }

  /**
   * Get empty state message based on current filter
   */
  getEmptyMessage(): string {
    if (this.searchString) {
      return this.translateService.instant('NO_ENVIRONMENTS_MATCH_SEARCH');
    }
    if (this.selectedFilter === 'configured') {
      return this.translateService.instant('NO_CONFIGURED_ENVIRONMENTS');
    }
    if (this.selectedFilter === 'notConfigured') {
      return this.translateService.instant('NO_UNCONFIGURED_ENVIRONMENTS');
    }
    return this.translateService.instant('NO_ENVIRONMENTS_FOUND');
  }
}
